import { effect, inject, Injectable, Signal, signal, WritableSignal } from '@angular/core';
import { SamlHttpService, TSamlPlatform } from '@app/http/saml-http.service';
import { AppNavigationService } from '@app/services/app-navigation.service';
import { AwsAmplifyService } from '@app/services/aws-amplify.service';
import { PolicyConsentService } from '@app/services/policy-consent.service';
import { UserService } from '@app/services/user.service';
import { KuiDialogService } from '@kone/ui';
import { translations } from '@utils/translations';
import { EMPTY, switchMap } from 'rxjs';

@Injectable({
    providedIn: 'root',
})
export class AppService {
    private kuiDialogService = inject(KuiDialogService);
    private awsAmplifyService = inject(AwsAmplifyService);
    private appNavigationService = inject(AppNavigationService);
    public userService = inject(UserService);
    private samlHttpService = inject(SamlHttpService);
    private policyConsentService = inject(PolicyConsentService);

    private _loading: WritableSignal<boolean> = signal(true);
    private _loadingLogout: WritableSignal<boolean> = signal(false);
    private _error: WritableSignal<boolean> = signal(false);
    private _menuState: WritableSignal<boolean> = signal(true);
    private logoutInProgress: Promise<void> | null = null;

    loading: Signal<boolean> = this._loading.asReadonly();
    loadingLogout: Signal<boolean> = this._loadingLogout.asReadonly();
    error: Signal<boolean> = this._error.asReadonly();
    menuState: Signal<boolean> = this._menuState.asReadonly();

    constructor() {
        effect(() => this.setImageBackground());
    }

    startLoading(): void {
        this._loading.set(true);
    }

    stopLoading(): void {
        this._loading.set(false);
    }

    hasError(): void {
        this._error.set(true);
    }

    clearError(): void {
        this._error.set(false);
    }

    triggerMenu() {
        this._menuState.set(!this._menuState());
    }

    logout(): Promise<void> {
        // Declining the policy consent and a failed start-up can both ask for a logout at once.
        this.logoutInProgress ??= this.performLogout().finally(
            () => (this.logoutInProgress = null),
        );
        return this.logoutInProgress;
    }

    private async performLogout() {
        this._loadingLogout.set(true);

        await new Promise(resolve => setTimeout(resolve, 450));

        await this.awsAmplifyService.signOut();
        this.kuiDialogService.closeAll();
        this.userService.clearUser();
        this.appNavigationService.navigateTo('auth');

        setTimeout(() => {
            this._loadingLogout.set(false);
        }, 200);
    }

    get bodyEl(): HTMLElement {
        return document.getElementById('body') as HTMLElement;
    }

    redirectTo(
        type: TSamlPlatform,
        disable?: boolean,
        loadingSpectrio?: WritableSignal<boolean>,
        loadingElevEight?: WritableSignal<boolean>,
        alertMsg?: WritableSignal<string>,
    ) {
        if (disable) return;
        const loading = type === 'spectrio' ? loadingSpectrio : loadingElevEight;
        loading?.set(true);
        alertMsg?.set('');

        // Ask explicitly instead of relying on the SAML request's 403: single-platform users land
        // here straight from login and must never reach Elev8/Spectrio without accepting.
        this.policyConsentService
            .ensureConsent()
            .pipe(
                switchMap(accepted => {
                    // Declining already logs the user out, there is nothing to report.
                    if (!accepted) {
                        loading?.set(false);
                        return EMPTY;
                    }
                    return this.samlHttpService.get(type);
                }),
            )
            .subscribe({
                next: response => {
                    if (!response) {
                        alertMsg?.set(translations.auth.landing.error);
                        loading?.set(false);
                        return;
                    }
                    const form = document.createElement('form');
                    form.method = 'POST';
                    form.action = response.destination;

                    const input = document.createElement('input');
                    input.type = 'hidden';
                    input.name = 'SAMLResponse';
                    input.value = response.base64;

                    form.appendChild(input);
                    document.body.appendChild(form);
                    form.submit();

                    setTimeout(() => {
                        loading?.set(false);
                    }, 3000);
                },
                error: err => {
                    alertMsg?.set(translations.auth.landing.error);
                    loading?.set(false);
                    console.error('Error:', err);
                },
            });
    }

    private setImageBackground(): void {
        const route = this.appNavigationService.currentRoute();
        this.bodyEl?.classList.toggle(
            'background-image',
            route === '/auth' || route.startsWith('/auth/') || route === '/information/nouser',
        );
    }
}
